import { useState } from "react";
import { MapPin, Mail, Clock, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { useSite } from "../context/SiteContext";
import { api } from "../lib/site";
import { Reveal, ChapterTag, SectionTitle } from "./Reveal";

export function Contact() {
  const { content, wa } = useSite();
  const s = content?.settings || {};
  const [form, setForm] = useState({ name: "", wa: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/leads", { type: "contact", name: form.name, wa: form.wa, message: form.message });
      window.open(wa(`Halo, saya ${form.name}. ${form.message}`), "_blank");
      toast.success("Pesan terkirim. Anda diarahkan ke WhatsApp.");
      setForm({ name: "", wa: "", message: "" });
    } catch {
      toast.error("Gagal mengirim. Silakan coba lagi.");
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full bg-navy-900 border border-glacier/20 rounded-sm px-4 py-3 text-sm text-frost placeholder:text-steel/50 focus:outline-none focus:border-ice transition-colors";

  return (
    <section id="kontak" data-testid="contact-section" className="relative py-24 sm:py-32 bg-navy-950">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <ChapterTag number="11" label="Kontak" />
        <div className="mb-14">
          <SectionTitle>Hubungi <span className="text-ice">Tim Kami</span></SectionTitle>
        </div>

        <div className="grid lg:grid-cols-2 gap-10">
          <Reveal>
            <div className="space-y-5">
              <h3 className="font-display text-2xl font-semibold uppercase text-frost" data-testid="contact-company-name">{s.company_name || "[Nama Perusahaan]"}</h3>
              {[
                { icon: MapPin, label: "Alamat", value: s.address || "[Alamat]", testid: "contact-address" },
                { icon: MessageCircle, label: "WhatsApp", value: s.wa_display || "[Nomor WhatsApp]", testid: "contact-wa", href: wa() },
                { icon: Mail, label: "Email", value: s.email || "[Email]", testid: "contact-email", href: s.email ? `mailto:${s.email}` : undefined },
                { icon: Clock, label: "Jam Operasional", value: s.hours || "[Senin–Sabtu, Jam]", testid: "contact-hours" },
              ].map((c) => (
                <div key={c.label} className="flex items-start gap-4 border border-glacier/15 rounded-sm p-5 bg-navy-900/60">
                  <span className="w-10 h-10 rounded-sm bg-navy-800 border border-glacier/25 flex items-center justify-center shrink-0">
                    <c.icon className="w-4.5 h-4.5 text-ice" />
                  </span>
                  <div>
                    <p className="font-mono text-[10px] tracking-[0.25em] text-steel uppercase">{c.label}</p>
                    {c.href ? (
                      <a data-testid={c.testid} href={c.href} target="_blank" rel="noopener noreferrer" className="text-sm text-frost mt-1 hover:text-ice transition-colors">{c.value}</a>
                    ) : (
                      <p data-testid={c.testid} className="text-sm text-frost mt-1">{c.value}</p>
                    )}
                  </div>
                </div>
              ))}
              {s.maps_url && (
                <div className="rounded-sm overflow-hidden border border-glacier/15" data-testid="contact-maps">
                  <iframe src={s.maps_url} title="Lokasi kami" className="w-full h-64" loading="lazy" />
                </div>
              )}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <form data-testid="contact-form" onSubmit={submit} className="bg-navy-900 border border-glacier/25 rounded-sm p-6 sm:p-8 space-y-4">
              <h3 className="font-display text-xl font-semibold uppercase text-frost">Kirim Pesan Cepat</h3>
              <input data-testid="contact-input-nama" required value={form.name} onChange={set("name")} placeholder="Nama *" className={inputCls} />
              <input data-testid="contact-input-wa" required value={form.wa} onChange={set("wa")} placeholder="Nomor WhatsApp *" className={inputCls} />
              <textarea data-testid="contact-input-pesan" required value={form.message} onChange={set("message")} placeholder="Tulis kebutuhan Anda *" rows={5} className={inputCls} />
              <button
                data-testid="contact-submit-btn"
                type="submit"
                disabled={sending}
                className="w-full flex items-center justify-center gap-2.5 bg-ice text-navy-950 font-semibold px-6 py-4 rounded-sm hover:bg-glacier transition-colors disabled:opacity-60"
              >
                <Send className="w-4 h-4" />
                {sending ? "Mengirim..." : "Kirim Pesan"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
